/**
 * #765 · **Un solo aviso por cada error del servidor.**
 *
 * Cada error que atiende el manejador global de la API llega por dos caminos a la vez: la
 * respuesta HTTP, que la pantalla pinta en su `catch` —casi siempre con `app.dialog.alert`, el
 * modal titulado «Simecal»—, y el evento SSE `error-servidor`, que `EventosErrorEvents` pinta en la
 * banda negra de abajo. Dos avisos con el mismo texto y dos clics para quitar lo mismo, en
 * cualquier pantalla: el `catch` está repetido en cientos de sitios, pero los dos caminos se cruzan
 * solo aquí, así que es aquí donde se decide cuál se queda.
 *
 * La regla es la que fijó la incidencia —se queda el que dice de qué va—:
 *
 *  - El modal con el título por defecto que cuenta **ese error en una frase** —el mensaje tal cual,
 *    o con una coletilla como «La propuesta sigue como provisional»— sobra: no se abre, y la banda
 *    pasa a decir el texto entero del modal, así que no se pierde nada. La banda es la que lleva
 *    «+ detalles» con la referencia.
 *  - El modal que dice **algo más que eso** se queda, y la banda se retira: el que lleva título
 *    propio, como «No se ha guardado» de `avisoNoGuardado`, que es un diálogo a propósito para que
 *    no se pierda de vista; el que tiene varias líneas, como el informe de fallos de un lote del
 *    calendario; o el que junta varios errores. A ese modal se le añade la referencia, para que el
 *    error siga siendo localizable.
 *
 * **Cuándo un modal y un aviso son el mismo error.** Hay un único contrato, y en él el texto nunca
 * es la identidad de un error:
 *
 *  - Un error es el mismo que otro **solo si comparten referencia**: la que pone la API en el
 *    evento y en la cabecera `X-Referencia-Error` de la respuesta (`DatabaseModel` la apunta aquí).
 *    Dos eventos sin referencia —la API anterior a la #765— son siempre dos errores, aunque digan
 *    lo mismo: dos fallos iguales seguidos son dos fallos, y cada uno tiene su aviso.
 *  - El texto solo sirve para **emparejar** un modal, que no trae más que su texto, con los errores
 *    que cuenta. El emparejamiento se apunta y es exclusivo: un error se empareja con un modal como
 *    mucho, y un modal con tantos errores de un mensaje como veces lo dice. Así un modal no se
 *    traga el siguiente fallo igual, y un error ya contado no vuelve a contarse en otro modal.
 *
 * Como el evento y la respuesta llegan por conexiones distintas y en cualquier orden, la banda
 * espera un momento antes de pintarse por si la pantalla está a punto de contestar, y el modal que
 * cede espera a la banda y, si no llega, se abre igual: nunca se queda un error sin aviso. El
 * emparejamiento, en cambio, vale toda la vigencia: un evento que llega tarde —una reconexión del
 * canal— sigue encontrando el modal que ya lo contó, y no saca la banda encima.
 *
 * **Todo eso es de una sesión.** Al cerrarla se olvida (`olvidar`, desde `EventosErrorEvents.stop`):
 * una espera que venciera después pintaría el aviso del usuario anterior en la pantalla de acceso o
 * en la sesión del siguiente, y sus errores y modales apuntados emparejarían —y callarían— los
 * avisos de quien entra.
 *
 * Y olvidar al cerrar no basta, porque lo de esa sesión puede seguir llegando después: una
 * respuesta que estaba en vuelo, o el modal que su `catch` abre en la pantalla de acceso. Por eso
 * lo que llega con la sesión cerrada no se apunta, y cada petición lleva la sesión de avisos en la
 * que salió (`sesion()`, que `DatabaseModel` toma al enviar): si al contestar es otra, su error no
 * se apunta, ni el modal que abra su pantalla. Esa pantalla sigue recibiendo el rechazo, como antes de
 * la #765; lo único que se descarta es su papel en el emparejamiento.
 */

/** Lo que espera la banda a que la pantalla decida: la respuesta HTTP sale justo detrás del evento. */
const ESPERA_BANDA_MS = 600;
/** Lo que espera un modal que ha cedido a que llegue la banda; si no llega, se abre él. */
const ESPERA_EVENTO_MS = 2000;
/** Hasta cuándo se emparejan por su texto un modal y un error. */
const VIGENCIA_MS = 15000;
/**
 * Hasta cuándo se recuerda un error con referencia. La referencia es única, así que un evento que
 * llega muy tarde sigue siendo, sin duda, el error del modal que ya se abrió.
 */
const VIGENCIA_REFERENCIA_MS = 120000;
/** Por debajo de esto, que un texto contenga el mensaje no dice nada. */
const LONGITUD_MINIMA = 8;
/** Lo que cabe en la banda: más largo, o con saltos de línea, ya es un informe y pide el modal. */
const LONGITUD_FRASE = 240;

let f7ref = null;
let pintarBanda = null;
/** Si hay una sesión abierta a la que pertenezca lo que llegue. Se cierra en `olvidar`. */
let activo = true;
/** Cuántas veces se ha cerrado sesión: una petición que salió con otro número es de una sesión ya ida. */
let sesionActual = 0;
const errores = [];
const modales = [];
/**
 * Los mensajes de respuestas de una sesión ya cerrada que han contestado con otra abierta: el modal
 * que abra su `catch` sale tal cual, pero sin apuntarse, o emparejaría —y callaría— un aviso de quien
 * está dentro ahora.
 */
const ajenos = [];

function ahora() { return Date.now(); }

function normalizar(texto) {
  return String(texto == null ? '' : texto).replace(/\s+/g, ' ').trim();
}

function esUnaFrase(texto) {
  const crudo = String(texto == null ? '' : texto);
  return !/<br|<p|<li|<div|\n/i.test(crudo) && normalizar(crudo).length <= LONGITUD_FRASE;
}

function purgarErrores() {
  const t = ahora();
  for (let i = errores.length - 1; i >= 0; i--) {
    const e = errores[i];
    const vigencia = e.referencia ? VIGENCIA_REFERENCIA_MS : VIGENCIA_MS;
    if (e.t < t - vigencia && !e.temporizador) errores.splice(i, 1);
  }
}

function purgarModales() {
  const limite = ahora() - VIGENCIA_MS;
  for (let i = modales.length - 1; i >= 0; i--) {
    if (modales[i].t < limite) modales.splice(i, 1);
  }
}

/** El error ya apuntado con esa referencia; sin referencia, o si no estaba, uno nuevo. */
function apuntar(referencia, mensaje) {
  purgarErrores();
  const m = normalizar(mensaje);
  let error = referencia ? errores.find(function (e) { return e.referencia === referencia; }) : null;
  if (error) {
    error.t = ahora();
  } else {
    error = { referencia: referencia || null, mensajes: [], t: ahora(), tRespuesta: 0, emparejado: false,
      mensajeDelModal: null, destino: null, banda: null, textoBanda: null, textoPintado: null,
      eventoLlegado: false, temporizador: null, alLlegar: null };
    errores.push(error);
  }
  if (m && !error.mensajes.includes(m)) error.mensajes.push(m);
  return error;
}

/** Si el texto es el de una respuesta ajena reciente; si lo es, la consume. */
function esDeOtraSesion(texto) {
  const limite = ahora() - ESPERA_EVENTO_MS;
  for (let i = ajenos.length - 1; i >= 0; i--) {
    if (ajenos[i].t < limite) ajenos.splice(i, 1);
  }
  const t = normalizar(texto);
  const i = ajenos.findIndex(function (a) { return veces(t, a.mensaje) > 0; });
  if (i === -1) return false;
  ajenos.splice(i, 1);
  return true;
}

/** Cuántas veces cuenta un texto ese mensaje: es lo que puede emparejar de él, y no más. */
function veces(texto, mensaje) {
  if (!mensaje) return 0;
  if (texto === mensaje) return 1;
  if (mensaje.length < LONGITUD_MINIMA) return 0;
  let n = 0;
  for (let i = texto.indexOf(mensaje); i !== -1; i = texto.indexOf(mensaje, i + mensaje.length)) n++;
  return n;
}

/** El primer mensaje del error que el texto todavía cuenta sin emparejar, o `null`. */
function mensajeLibre(error, texto, usos) {
  return error.mensajes.find(function (m) { return veces(texto, m) > (usos[m] || 0); }) || null;
}

/**
 * Los errores que cuenta el texto de un modal y que no ha contado ya otro. Si hay varios iguales,
 * va primero el de la respuesta que acaba de llegar —es la que ha abierto este modal— y luego el más
 * antiguo; y de cada mensaje, tantos como veces lo dice el modal.
 */
function erroresDelTexto(texto) {
  purgarErrores();
  const t = normalizar(texto);
  const desde = ahora() - VIGENCIA_MS;
  const usos = {};
  return errores
    .filter(function (e) { return !e.emparejado && e.t >= desde; })
    .sort(function (a, b) { return (b.tRespuesta - a.tRespuesta) || (a.t - b.t); })
    .filter(function (e) {
      const m = mensajeLibre(e, t, usos);
      if (!m) return false;
      usos[m] = (usos[m] || 0) + 1;
      e.mensajeDelModal = m;
      return true;
    });
}

/**
 * El modal ya abierto que cuenta este error y le queda sitio para él. Es el caso de una respuesta
 * que no pasa por `DatabaseModel` —no trae la referencia— y llega antes que el evento: el modal se
 * ha abierto sin saber nada de la banda, y la que sobra es ella. Se busca en toda la vigencia, no
 * solo en el momento en que llega el evento, que puede retrasarse lo que tarde el canal en volver.
 */
function emparejarConModalAbierto(error) {
  purgarModales();
  for (const modal of modales) {
    const m = mensajeLibre(error, modal.texto, modal.usos);
    if (m) {
      modal.usos[m] = (modal.usos[m] || 0) + 1;
      error.emparejado = true;
      error.destino = 'modal';
      return true;
    }
  }
  return false;
}

function cancelarTemporizador(error) {
  if (error.temporizador) {
    clearTimeout(error.temporizador);
    error.temporizador = null;
  }
}

function retirarBanda(error) {
  cancelarTemporizador(error);
  error.destino = 'modal';
  error.alLlegar = null;
  if (error.banda && typeof error.banda.close === 'function') error.banda.close();
  error.banda = null;
}

function mostrarBanda(error) {
  cancelarTemporizador(error);
  error.destino = 'banda';
  const deLaPantalla = !!error.textoBanda;
  const texto = error.textoBanda || error.mensajes[0] || '';
  if (error.banda && error.textoPintado !== texto) {
    // Ya estaba con el mensaje del servidor y la pantalla ha dicho algo más: se repinta entera.
    if (typeof error.banda.close === 'function') error.banda.close();
    error.banda = null;
  }
  if (!error.banda && pintarBanda) {
    error.banda = pintarBanda(texto, error.referencia, deLaPantalla);
    error.textoPintado = texto;
  }
  const alLlegar = error.alLlegar;
  error.alLlegar = null;
  if (alLlegar) alLlegar();
}

function tituloPorDefecto() {
  const p = f7ref && f7ref.params;
  return (p && p.dialog && p.dialog.title) || (f7ref && f7ref.name) || (p && p.name);
}

function conReferencias(texto, encontrados) {
  const refs = encontrados.map(function (e) { return e.referencia; }).filter(Boolean);
  if (!refs.length) return texto;
  return texto + '<br><br><small>Referencia' + (refs.length > 1 ? 's' : '') + ' del error: '
    + refs.join(', ') + '</small>';
}

/**
 * El `dialog.alert` de Framework7, con la decisión delante. Admite sus dos firmas:
 * `(texto, titulo, alAceptar)` y `(texto, alAceptar)`.
 */
function envolverAlert(alertOriginal) {
  return function (texto, titulo, alAceptar) {
    if (arguments.length === 2 && typeof titulo === 'function') {
      alAceptar = titulo;
      titulo = undefined;
    }
    // El modal que se abre queda apuntado con los errores que ya cuenta, para que un evento que
    // llegue después solo pueda emparejarse con lo que le quede sin contar.
    const abrir = function (textoFinal, contados) {
      const usos = {};
      contados.forEach(function (e) { usos[e.mensajeDelModal] = (usos[e.mensajeDelModal] || 0) + 1; });
      modales.push({ texto: normalizar(texto), t: ahora(), usos: usos });
      return titulo === undefined
        ? alertOriginal(textoFinal, alAceptar)
        : alertOriginal(textoFinal, titulo, alAceptar);
    };
    // Con la sesión cerrada, o si es el de una respuesta de la anterior, no se apunta ni se empareja:
    // apuntado, callaría el aviso igual de quien está o entra detrás.
    if (!activo || esDeOtraSesion(texto)) {
      return titulo === undefined ? alertOriginal(texto, alAceptar) : alertOriginal(texto, titulo, alAceptar);
    }
    const encontrados = erroresDelTexto(texto);
    if (!encontrados.length) return abrir(texto, []);
    encontrados.forEach(function (e) { e.emparejado = true; });

    const porDefecto = titulo === undefined || titulo === null || titulo === tituloPorDefecto();
    if (!porDefecto || encontrados.length > 1 || !esUnaFrase(texto)) {
      encontrados.forEach(retirarBanda);
      return abrir(conReferencias(texto, encontrados), encontrados);
    }

    // El modal cede. Se devuelve un diálogo sin abrir porque hay quien encadena sobre lo que
    // devuelve `alert`; y quien esperaba al «OK» para seguir, sigue.
    const error = encontrados[0];
    error.textoBanda = texto;
    const sinAbrir = f7ref.dialog.create({
      title: titulo === undefined ? tituloPorDefecto() : titulo,
      text: texto,
      buttons: [{ text: f7ref.params.dialog.buttonOk, bold: true, onClick: alAceptar }],
      destroyOnClose: true
    });
    const continuar = function () { if (typeof alAceptar === 'function') setTimeout(alAceptar, 0); };
    if (error.eventoLlegado) {
      error.alLlegar = continuar;
      mostrarBanda(error);
    } else {
      // Ha llegado antes la respuesta que el evento: se espera al evento y, si no llega, el modal.
      cancelarTemporizador(error);
      error.destino = 'banda';
      error.alLlegar = continuar;
      error.temporizador = setTimeout(function () {
        error.temporizador = null;
        error.destino = 'modal';
        error.alLlegar = null;
        abrir(conReferencias(texto, [error]), [error]);
      }, ESPERA_EVENTO_MS);
    }
    return sinAbrir;
  };
}

export default {
  /**
   * Engancha la decisión en el `dialog.alert` de la aplicación (una vez) y recibe cómo se pinta la
   * banda: `pintar(texto, referencia, deLaPantalla)` devuelve el toast abierto, para poder retirarlo.
   */
  instalar(f7, pintar) {
    if (!f7 || !f7.dialog) return;
    f7ref = f7;
    pintarBanda = pintar;
    activo = true;
    if (f7.dialog.alert && !f7.dialog.alert.__avisoUnico) {
      const envuelto = envolverAlert(f7.dialog.alert.bind(f7.dialog));
      envuelto.__avisoUnico = true;
      f7.dialog.alert = envuelto;
    }
  },

  /** La sesión de avisos en curso: `DatabaseModel` la toma al enviar y la devuelve en `desdeRespuesta`. */
  sesion() {
    return sesionActual;
  },

  /**
   * La respuesta HTTP de un error que la API también ha mandado como aviso (`X-Referencia-Error`).
   * `sesion` es la de cuando salió la petición: si desde entonces se ha cerrado, no se apunta, y el
   * modal que abra su pantalla tampoco, lleve referencia o no.
   */
  desdeRespuesta(referencia, mensaje, sesion) {
    if (!activo) return;
    if (sesion !== undefined && sesion !== sesionActual) {
      const m = normalizar(mensaje);
      if (m) ajenos.push({ mensaje: m, t: ahora() });
      return;
    }
    if (!referencia) return;
    apuntar(referencia, mensaje).tRespuesta = ahora();
  },

  /** El evento `error-servidor`: `{ message, referencia }`. */
  desdeServidor(payload) {
    if (!activo) return;
    const mensaje = (payload && payload.message) || 'Error en el servidor';
    const error = apuntar(payload && payload.referencia, mensaje);
    error.eventoLlegado = true;
    if (error.destino === 'modal') return;
    if (error.destino === 'banda') {
      mostrarBanda(error);
      return;
    }
    cancelarTemporizador(error);
    error.temporizador = setTimeout(function () {
      error.temporizador = null;
      if (error.destino) return;
      // Si su respuesta ya ha llegado, su modal es el que esté por abrir, no uno anterior.
      if (!error.tRespuesta && emparejarConModalAbierto(error)) return;
      mostrarBanda(error);
    }, ESPERA_BANDA_MS);
  },

  /**
   * Al cerrar la sesión: ninguna espera vence ya, las bandas que siguen abiertas se retiran y lo
   * apuntado se vacía. El modal que había cedido no se abre, ni sigue quien esperaba a su «OK»: esa
   * pantalla era de la sesión que se ha ido. La instalación, en cambio, se queda: es de la aplicación.
   * Hasta que `instalar` abra la siguiente, no se apunta nada, y lo que salió antes ya no se apuntará.
   */
  olvidar() {
    activo = false;
    sesionActual++;
    errores.forEach(function (error) {
      cancelarTemporizador(error);
      error.alLlegar = null;
      if (error.banda && typeof error.banda.close === 'function') error.banda.close();
      error.banda = null;
    });
    errores.length = 0;
    modales.length = 0;
    ajenos.length = 0;
  }
};
